import { useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { CheckCircle2, Loader2, RotateCw, Sparkles, X, XCircle } from "lucide-react";
import { errMsg } from "./paywall";

const md = (c) => (
  <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>{c}</ReactMarkdown>
);

export const TutorQuiz = ({ api, authConfig, session, onClose, onReteach, onResult }) => {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  async function generate() {
    setBusy(true); setErr(""); setResult(null); setAnswers({});
    try {
      const r = await axios.post(`${api}/tutor/sessions/${session.session_id}/quiz`, {}, authConfig);
      setQuiz(r.data.quiz);
    } catch (e) { setErr(errMsg(e, "Could not prepare the quiz.")); }
    finally { setBusy(false); }
  }

  async function submit() {
    setSubmitting(true); setErr("");
    try {
      const arr = quiz.questions.map((_, i) => answers[i] ?? -1);
      const r = await axios.post(`${api}/tutor/quizzes/${quiz.quiz_id}/submit`, { answers: arr }, authConfig);
      setResult(r.data);
      onResult?.(r.data);
    } catch (e) { setErr(errMsg(e, "Submit failed.")); }
    finally { setSubmitting(false); }
  }

  const allAnswered = quiz && Object.keys(answers).length === quiz.questions.length;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="quiz-overlay">
      <div className="w-full max-w-2xl bg-[#0A0A0A] border border-zinc-800 rounded-2xl p-5 sm:p-6 relative">
        <button onClick={onClose} data-testid="quiz-close-btn" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={18}/></button>
        <p className="text-cyan-300 text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-2"><Sparkles size={13}/> Practice quiz</p>
        <h3 className="text-white font-bold text-lg mb-4">{session.chapter}</h3>

        {err && (
          <div className="mb-4">
            <p className="text-red-300 text-sm mb-3" data-testid="quiz-error">⚠ {err}</p>
            <button onClick={generate} className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-white hover:border-cyan-400"><RotateCw size={12}/> Try again</button>
          </div>
        )}

        {busy ? (
          <div className="py-14 text-center">
            <Loader2 className="animate-spin text-cyan-400 mx-auto mb-3" size={24}/>
            <p className="text-zinc-400 text-sm">Teacher is preparing 5 questions for you…</p>
          </div>
        ) : result ? (
          <div data-testid="quiz-result">
            <div className={`rounded-xl p-4 mb-5 text-center border ${result.wrong_count === 0 ? "bg-emerald-500/10 border-emerald-400/40" : "bg-cyan-500/10 border-cyan-400/30"}`}>
              <p className="text-3xl font-black text-white" data-testid="quiz-score">{result.score}/{result.total}</p>
              <p className="text-zinc-300 text-sm mt-1">{result.wrong_count === 0 ? "Perfect score — brilliant! 🌟" : `${result.wrong_count} to revise — teacher will help!`}</p>
            </div>
            <div className="space-y-3 mb-5">
              {result.results.map((r, i) => (
                <div key={i} className={`rounded-xl border p-3.5 ${r.correct ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`} data-testid={`quiz-result-q${i}`}>
                  <div className="flex items-start gap-2 text-sm text-zinc-100">
                    {r.correct ? <CheckCircle2 size={15} className="text-emerald-400 mt-0.5 shrink-0"/> : <XCircle size={15} className="text-red-400 mt-0.5 shrink-0"/>}
                    <div className="prose prose-invert prose-sm max-w-none [&_p]:my-0">{md(`**Q${i + 1}.** ${r.q}`)}</div>
                  </div>
                  {!r.correct && (
                    <div className="mt-2 ml-6 text-xs space-y-0.5">
                      <p className="text-red-300">Your answer: {r.your_index >= 0 ? r.options[r.your_index] : "—"}</p>
                      <p className="text-emerald-300">Correct: {r.options[r.correct_index]}</p>
                      {r.explanation && <p className="text-zinc-400">{r.explanation}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {result.wrong_count > 0 && (
                <button onClick={() => onReteach(result.quiz_id)} data-testid="quiz-reteach-btn"
                  className="px-4 py-2.5 rounded-full text-sm font-bold text-black" style={{ background: "linear-gradient(135deg, #00F0FF, #22D3EE)" }}>
                  Re-teach my mistakes ({result.wrong_count})
                </button>
              )}
              {result.wrong_count === 0 && (
                <button onClick={() => onReteach(result.quiz_id)} data-testid="quiz-challenge-btn"
                  className="px-4 py-2.5 rounded-full text-sm font-bold text-black" style={{ background: "linear-gradient(135deg, #00F0FF, #22D3EE)" }}>
                  Give me a challenge question
                </button>
              )}
              <button onClick={onClose} data-testid="quiz-done-btn" className="px-4 py-2.5 rounded-full text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500">Close</button>
            </div>
          </div>
        ) : quiz ? (
          <div data-testid="quiz-questions">
            <div className="space-y-5 mb-5">
              {quiz.questions.map((q, i) => (
                <div key={i} data-testid={`quiz-q${i}`}>
                  <div className="text-sm text-zinc-100 mb-2 prose prose-invert prose-sm max-w-none [&_p]:my-0">{md(`**Q${i + 1}.** ${q.q}`)}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {q.options.map((opt, oi) => (
                      <button key={oi} onClick={() => setAnswers({ ...answers, [i]: oi })} data-testid={`quiz-q${i}-opt${oi}`}
                        className={`text-left px-3 py-2 rounded-lg border text-xs transition-colors ${answers[i] === oi
                          ? "bg-cyan-500/15 border-cyan-400 text-cyan-100"
                          : "border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}>
                        <span className="prose prose-invert prose-sm max-w-none [&_p]:my-0 [&_p]:inline">{md(opt)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={submit} disabled={!allAnswered || submitting} data-testid="quiz-submit-btn"
              className="w-full py-3 rounded-full text-sm font-bold text-black disabled:opacity-40"
              style={{ background: "linear-gradient(135deg, #00F0FF, #22D3EE)" }}>
              {submitting ? <Loader2 size={14} className="animate-spin inline"/> : allAnswered ? "Submit answers" : `Answer all ${quiz.questions.length} questions`}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
